import { Checkbox } from "shared/UI/Checkbox/Checkbox"
import PageTitle from "UIPalette/PageTitle/PageTitle";
import { ViewStateWrapper } from "UIPalette/ViewStateWrapper/ViewStateWrapper";
import { useUserContext } from "core/state/UserContext/UserContext";
import { t } from "i18next";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import Button from "shared/UI/Buttons/Button";
import { DestinationState } from "shared/core/state/destinationState";
import { useGetUserInterestPreferencesFunctionRequest, useUpdateUserInterestsFunctionRequest } from "shared/request/myHealthyAdvantageApi";

export const Preferences = () => {
  const [dislayErrorMessage, setDislayErrorMessage] = useState(false);
  const [selectedPreferences, setSelectedPreferences] = useState<string[]>([]);
  const navigate = useNavigate();
  const { data, error, isLoading, mutate: refresh } = useGetUserInterestPreferencesFunctionRequest({});
  const { trigger } = useUpdateUserInterestsFunctionRequest({});
  const { user, updateUserInterests } = useUserContext();
  const userHasInterests = user && (user.interests.length ?? 0) !== 0;

  const sortedPreferences = useMemo(() => {
    const preferences = !!data?.interestPreferences ? [...data?.interestPreferences] : [];
    return preferences.sort((a, b) => a.name.localeCompare(b.name));
  }, [data?.interestPreferences]);

  const savePreference = async () => {
    try {
      const response = await trigger({ body: { interestCategoryIds: selectedPreferences } });
      updateUserInterests(response?.data.interests ?? []);
      await refresh();
      navigate("/home", { replace: true });
      navigate(DestinationState.get());
    } catch {
      setDislayErrorMessage(true);
    }
  };

  const updateInterests = (preferenceCategoryId: string, isSelected: boolean) => {
    let preferencesToUpdate = [...selectedPreferences];
    if (isSelected) {
      preferencesToUpdate = [...preferencesToUpdate, preferenceCategoryId];
    } else {
      preferencesToUpdate = preferencesToUpdate.filter((x) => x !== preferenceCategoryId);
    }
    setSelectedPreferences(preferencesToUpdate);
  };

  if (userHasInterests) {
    navigate("/home", { replace: true });
    navigate(DestinationState.get());
  }

  return (
    <>
      <PageTitle title={t("pageTitle.preferences")} />
      <ViewStateWrapper loading={isLoading} error={!!error} errorMessage={t("preferences.error")} onRetry={refresh} isFullScreenView={true}>
        <p className="mb-4">{t("preferences.intro")}</p>
        <div className="flex flex-col mb-6">
          {sortedPreferences.map((interest) => (
            <div className="py-2" key={interest.categoryId}>
              <Checkbox
                checked={selectedPreferences.includes(interest.categoryId)}
                onChange={(e) => updateInterests(interest.categoryId, e.target.checked)}
                label={interest.name}
              />
            </div>
          ))}
        </div>
        <Button
          disabled={selectedPreferences.length === 0}
          text={t("preferences.save")}
          onClick={async () => await savePreference()}
        />
        {dislayErrorMessage && <ErrorAlert content={t("preferences.updateFailure")} />}
      </ViewStateWrapper>
    </>
  );
};
